/**
 * Sanitize paragraph HTML to a minimal whitelist: b, br, u, i, em, plus
 * safe links (`<a href>` with scheme check, all other attributes dropped)
 * and inline icon markers (`<span data-icon="name"></span>` — see
 * `inlineIcons.ts` for the render-time expansion). strong is normalized
 * to b; any other span and every other tag is removed (text kept).
 * Use for paragraphs, titles, accordion content.
 *
 * MIRRORED in BCC.ADMIN.React/src/Components/PageEditor/paragraphHtml.ts —
 * the admin sanitises the same HTML at save time. Keep the two
 * `sanitizeParagraphHtml` implementations byte-identical (a jest parity
 * test compares their outputs over a fixture set).
 */

/** Valid inline icon name (see inlineIcons.ts for the catalogue). */
const ICON_NAME = /^[a-z0-9-]{1,32}$/;

/** NUL sentinel delimiting stashed sanitised tags during the generic pass. */
const NUL = String.fromCharCode(0);

/**
 * Pulls a single attribute value out of a raw tag's attribute string.
 * Matches `name="…"`, `name='…'` and bare `name=value` forms.
 */
function readAttr(attrs: string, name: string): string | null {
  const re = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`, "i");
  const m = re.exec(attrs);
  if (!m) return null;
  return m[1] ?? m[2] ?? m[3] ?? "";
}

/** True when an href is safe to keep (no javascript:/vbscript:/data: scheme). */
function isSafeHref(href: string): boolean {
  const v = href.trim().toLowerCase();
  // Control characters and whitespace are removed before the scheme check so
  // "jav\tascript:" cannot smuggle a scheme past the regex.
  const stripped = Array.from(v)
    .filter((ch) => ch.charCodeAt(0) > 32)
    .join("");
  return !/^(?:javascript|vbscript|data):/i.test(stripped);
}

/** Escapes a string for safe inclusion inside a double-quoted attribute. */
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function sanitizeParagraphHtml(html: string): string {
  if (typeof html !== "string" || !html.trim()) return "";

  // Sanitised anchor/icon tags are stashed as NUL-delimited index tokens so
  // the generic tag pass below cannot re-process (and double-escape) them,
  // and so an unpaired/unsafe <a> or <span> is stripped instead of surviving.
  const tokens: string[] = [];
  const stash = (s: string): string => {
    tokens.push(s);
    return NUL + (tokens.length - 1) + NUL;
  };

  // The NUL delimiter cannot pre-exist in honest input; drop it if present.
  let out = html.split(NUL).join("");

  // PAIRED anchors: kept with a safe href only (all other attributes
  // dropped); unsafe or missing href unwraps the tag and keeps the text.
  out = out.replace(
    /<a\b((?:[^>"']|"[^"]*"|'[^']*')*)>([\s\S]*?)<\/a\s*>/gi,
    (_match, attrs: string, inner: string) => {
      const href = readAttr(attrs, "href");
      if (href != null && href.trim() && isSafeHref(href)) {
        return stash(`<a href="${escapeAttr(href.trim())}">`) + inner + stash("</a>");
      }
      return inner;
    },
  );

  // PAIRED icon spans: normalised to an empty `<span data-icon="name"></span>`
  // (children dropped); any other span unwraps to its text.
  out = out.replace(
    /<span\b((?:[^>"']|"[^"]*"|'[^']*')*)>([\s\S]*?)<\/span\s*>/gi,
    (_match, attrs: string, inner: string) => {
      const icon = readAttr(attrs, "data-icon")?.trim().toLowerCase();
      if (icon && ICON_NAME.test(icon)) {
        return stash(`<span data-icon="${icon}"></span>`);
      }
      return inner;
    },
  );

  // Generic pass: keep the simple whitelist (attributes stripped, strong→b);
  // everything else — including stray unpaired <a>/<span> — is removed.
  out = out.replace(/<\/?([a-z][a-z0-9]*)(?:\s[^>]*)?\/?>/gi, (match, tagName: string) => {
    const tag = (tagName || "").toLowerCase();
    if (!/^(?:b|strong|br|u|i|em)$/.test(tag)) return "";
    return match;
  });
  out = out
    .replace(/<(strong)(?:\s[^>]*)?>/gi, "<b>")
    .replace(/<\/(strong)>/gi, "</b>")
    .replace(/<(b)(?:\s[^>]*)?>/gi, "<b>")
    .replace(/<\/(b)>/gi, "</b>")
    .replace(/<(br)(?:\s[^>]*)?\/?>/gi, "<br>")
    .replace(/<(u)(?:\s[^>]*)?>/gi, "<u>")
    .replace(/<\/(u)>/gi, "</u>")
    .replace(/<(i)(?:\s[^>]*)?>/gi, "<i>")
    .replace(/<\/(i)>/gi, "</i>")
    .replace(/<(em)(?:\s[^>]*)?>/gi, "<em>")
    .replace(/<\/(em)>/gi, "</em>");

  // Restore the sanitised anchor/icon tags.
  out = out.replace(new RegExp(NUL + "(\\d+)" + NUL, "g"), (_m, i: string) => tokens[Number(i)] ?? "");
  return out;
}

/** Returns true if the string looks like HTML (contains a tag). */
export function isParagraphHtml(s: string): boolean {
  return typeof s === "string" && /<[a-z]/i.test(s);
}
